'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useMemo } from 'react';
import { Dropdown, DropdownOption, c } from 'ui-components';
import { useSearchV2Sort } from '../hooks/useSearchV2Sort';
import { SortProps } from './Sort.types';

export const Sort = ({ className }: SortProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const sortByLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.sort-by')?.value ??
    'Sort by';

  const { currentSortValue, sortCriteria, handleSort } = useSearchV2Sort();

  const options: DropdownOption[] = useMemo(
    () =>
      sortCriteria.map((opt) => {
        const key = `search.${opt.value}`;
        const label =
          searchLabelsMicrocopy?.find((m) => m.key === key)?.value ??
          opt.value.charAt(0).toUpperCase() + opt.value.slice(1);
        return { label, value: opt.value };
      }),
    [searchLabelsMicrocopy, sortCriteria]
  );

  const baseOption =
    options.find((opt) => opt.value === currentSortValue) ?? options[0];

  const selectedOption: DropdownOption = useMemo(
    () => ({
      ...baseOption,
      label: (
        <>
          <span className="text-grey-600">{sortByLabel}:</span>{' '}
          <span className="font-normal text-[#141414] opacity-90">
            {baseOption.label}
          </span>
        </>
      ),
    }),
    [baseOption, sortByLabel]
  );

  return (
    <div className={c('min-w-[180px]', className)}>
      <Dropdown
        id="search-sort"
        options={options}
        selectedOption={selectedOption}
        onSelect={(option) => handleSort(option.value)}
        isDarkTheme={false}
        buttonClassName="bg-transparent"
        ariaLabel={`Sort by ${baseOption.label}`}
      />
    </div>
  );
};
